import {
  faArrowRight,
  faCartPlus,
  faLock,
  faMoneyBill1Wave,
} from '@fortawesome/free-solid-svg-icons';
import { useMemo } from 'react';

import { clientStatuses } from '@app/components/RecordModal/components/RecordDetails/data/constants';
import { ClientStatusEnum } from '@app/components/RecordModal/components/RecordDetails/types';
import { getTagsOptions } from '@app/components/RecordModal/components/RecordDetails/utils';
import { RecordStatusColor, TAGS_COLORS, WAITING } from '@app/constants';
import { getContrastYIQ } from '@app/utils';

import type { CalendarEvent } from '@pages/Calendar/data';
import { getRecordDisplayIdentity } from '@pages/Calendar/utils/getRecordDisplayIdentity';

import { stopCardInteraction } from '../utils';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface UseAppointmentCardModelProps {
  event: CalendarEvent;
  previewHeightPx: number;
  canShowContacts: boolean;
}

interface StatusIconItem {
  key: string;
  icon: IconDefinition;
  title: string;
}

interface UseAppointmentCardModelResult {
  bodyBackgroundColor: string;
  borderAccentColor: string;
  clientName: string;
  contentTextColor: string;
  headerBackgroundColor: string;
  hiddenServicesCount: number;
  hiddenTagsCount: number;
  isExtended: boolean;
  isFirstVisit: boolean;
  isLoyaltyCardVisible: boolean;
  phone?: string;
  previewShadow: string;
  primaryText: string;
  secondaryText?: string;
  showComment: boolean;
  showPhone: boolean;
  showServices: boolean;
  showTags: boolean;
  statusIcon?: StatusIconItem;
  statusIconColor?: string;
  statusIcons: StatusIconItem[];
  tagsOptions: ReturnType<typeof getTagsOptions>;
  visibleComment?: string;
  visibleServices: string[];
  visibleTags: ReturnType<typeof getTagsOptions>;
  stopCardInteraction: typeof stopCardInteraction;
}

const formatPhone = (phone?: string): string | undefined => {
  if (!phone) {
    return undefined;
  }

  return phone.startsWith('+') ? phone : `+${phone}`;
};

export const useAppointmentCardModel = ({
  event,
  previewHeightPx,
  canShowContacts,
}: UseAppointmentCardModelProps): UseAppointmentCardModelResult => {
  const { rawRecord } = event;
  const status = rawRecord.Status || WAITING;
  const statusBackgroundColor =
    RecordStatusColor[status as keyof typeof RecordStatusColor] ||
    RecordStatusColor[WAITING];
  const bodyBackgroundColor = rawRecord.RecordColor || statusBackgroundColor;
  const contentTextColor = getContrastYIQ(bodyBackgroundColor);

  const { clientName, phone } = useMemo(
    () =>
      getRecordDisplayIdentity({
        record: rawRecord,
        canShowContacts,
        fallbackClientName: event.clientName || event.title,
        fallbackPhone: event.phone,
      }),
    [canShowContacts, event.clientName, event.phone, event.title, rawRecord]
  );

  const services = useMemo(() => {
    const serviceNames =
      rawRecord.ClientServices?.filter((service) => Boolean(service.Name)).map(
        (service) => service.Name!
      ) ?? [];

    return serviceNames.length > 0 ? serviceNames : event.services ?? [];
  }, [event.services, rawRecord.ClientServices]);

  const primaryText = services[0] ?? event.title;
  const secondaryText = services.length > 1 ? services[1] : undefined;

  const isExtended = previewHeightPx >= 118;
  const showPhone = Boolean(phone);
  const showServices = services.length > 0;

  const tagsOptions = useMemo(
    () => getTagsOptions(rawRecord.Tags ?? []),
    [rawRecord.Tags]
  );

  const showTags = tagsOptions.length > 0;
  const showComment = Boolean(rawRecord.Comment);
  const hiddenServicesCount = 0;
  const hiddenTagsCount = 0;

  const previewShadow = useMemo(() => {
    const groupShadowColor = event.clientGroupId
      ? TAGS_COLORS[event.clientGroupId] || 'rgba(255, 255, 255, 0.36)'
      : 'rgba(255, 255, 255, 0.36)';

    return `0 0 10px 2px ${groupShadowColor}`;
  }, [event.clientGroupId]);

  const statusIcons = useMemo(() => {
    const items: StatusIconItem[] = [];

    if ((rawRecord.ClientFutureRecordIds?.length ?? 0) > 0) {
      items.push({
        key: 'future-records',
        icon: faArrowRight,
        title: 'Есть записи в будущем',
      });
    }

    if (
      rawRecord.ClientServices?.some(
        (service) => service.UserId !== null && service.UserId !== undefined
      )
    ) {
      items.push({
        key: 'upsell',
        icon: faCartPlus,
        title: 'Есть допродажи',
      });
    }

    if (rawRecord.IsPaid) {
      items.push({
        key: 'paid',
        icon: faMoneyBill1Wave,
        title: 'Запись полностью оплачена',
      });
    }

    if (rawRecord.IsEmployeeLocked) {
      items.push({
        key: 'employee-locked',
        icon: faLock,
        title: 'Исполнителя нельзя менять',
      });
    }

    return items;
  }, [
    rawRecord.ClientFutureRecordIds,
    rawRecord.ClientServices,
    rawRecord.IsEmployeeLocked,
    rawRecord.IsPaid,
  ]);

  // Иконка статуса вынесена из общего списка: она рендерится отдельной
  // кнопкой, потому что несёт popover с деталями записи и подсветку карты
  // лояльности.
  const statusIcon = useMemo(() => {
    const statusData =
      clientStatuses.find((item) => item.status === status) ??
      clientStatuses.find((item) => item.status === ClientStatusEnum.WAITING);

    if (!statusData) return undefined;

    return {
      key: `status-${status}`,
      icon: statusData.icon,
      title: statusData.label,
    };
  }, [status]);

  const visibleComment = rawRecord.Comment || undefined;

  return {
    bodyBackgroundColor,
    borderAccentColor: rawRecord.IsPaid ? '#18a6af' : '#E14F4F',
    clientName,
    contentTextColor,
    headerBackgroundColor: rawRecord.IsPaid ? '#18a6af' : '#E14F4F',
    hiddenServicesCount,
    hiddenTagsCount,
    isExtended,
    isFirstVisit: Boolean(rawRecord.IsFirstVisit),
    isLoyaltyCardVisible: Boolean(rawRecord.ClientHasLoyaltyCard),
    phone: formatPhone(phone),
    previewShadow,
    primaryText,
    secondaryText,
    showComment,
    showPhone,
    showServices,
    showTags,
    statusIcon,
    statusIconColor: rawRecord.ClientHasLoyaltyCard
      ? 'rgb(156, 39, 176)'
      : undefined,
    statusIcons,
    stopCardInteraction,
    tagsOptions,
    visibleComment,
    visibleServices: services,
    visibleTags: tagsOptions,
  };
};
